import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UploadZone from '../components/UploadZone';
import { uploadContract } from '../utils/api';
import { useAuth } from '../utils/AuthContext';

const Upload = () => {
  const [file, setFile] = useState(null);
  const [contractType, setContractType] = useState('other');
  const [contractCountry, setContractCountry] = useState('US');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const contractTypes = [
    { value: 'property', label: 'Property Sale/Purchase', icon: '🏠' },
    { value: 'lease', label: 'Lease Agreement', icon: '📋' },
    { value: 'employment', label: 'Employment Contract', icon: '💼' },
    { value: 'freelance', label: 'Freelance Agreement', icon: '✍️' },
    { value: 'vendor', label: 'Vendor Agreement', icon: '🤝' },
    { value: 'nda', label: 'Non-Disclosure Agreement', icon: '🔒' },
    { value: 'service', label: 'Service Agreement', icon: '⚙️' },
    { value: 'other', label: 'Other', icon: '📄' }
  ];

  const countries = [
    { code: 'US', name: 'United States' },
    { code: 'UK', name: 'United Kingdom' },
    { code: 'CA', name: 'Canada' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'ES', name: 'Spain' },
    { code: 'OTHER', name: 'Other' }
  ];

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setError('');
  };

  const handleRemoveFile = () => {
    setFile(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('contract_type', contractType);
    formData.append('contract_country', contractCountry);

    try {
      const response = await uploadContract(formData);
      await refreshUser();
      navigate(`/results/${response.data.analysis_id}`);
    } catch (err) {
      if (err.response?.status === 402) {
        setError('You have no scans remaining. Please upgrade your plan.');
      } else {
        // Show the detailed error message, not just the error name
        const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Upload failed. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2">Upload Contract for Analysis</h1>
          <p className="text-gray-600">
            Upload your contract and get AI-powered analysis in seconds
          </p>
          {user && (
            <p className="text-sm text-gray-500 mt-2">
              Scans remaining: <span className="font-semibold">{user.scans_remaining}</span>
            </p>
          )}
        </div>

        <div className="min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Country/Jurisdiction
          </label>
          <select
            value={contractCountry}
            onChange={(e) => setContractCountry(e.target.value)}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4">
            Select Contract Type
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {contractTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setContractType(type.value)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  contractType === type.value
                    ? 'border-primary-600 bg-primary-50 shadow-md'
                    : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                }`}
              >
                <div className="text-3xl mb-2">{type.icon}</div>
                <div className="text-sm font-medium text-gray-900">{type.label}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload File
          </label>
          {!file ? (
            <UploadZone onFileSelect={handleFileSelect} />
          ) : (
            <div className="border-2 border-green-300 bg-green-50 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-600">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="text-red-600 hover:text-red-800 p-2"
                  title="Remove file"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {loading && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">Analysing your contract...</span>
              <span className="text-sm text-blue-700">This may take 30-60 seconds</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
              <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
            </div>
            <p className="text-xs text-blue-700 mt-2">Extracting text, analyzing clauses, and identifying risks...</p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !file}
          className="w-full bg-primary-600 text-white hover:bg-primary-700 py-3 px-6 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Analysing Contract...' : 'Analyse Contract'}
        </button>

        <div className="mt-6 space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong className="text-blue-800">🔒 Your Privacy Matters:</strong> We do not cache or store your contract content beyond what's necessary to provide analysis. Your contracts are processed securely and not retained in our systems.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong className="text-yellow-800">⚠️ Legal Disclaimer:</strong> ContractGuard provides AI-powered analysis for informational purposes only.
              This is NOT legal advice. Always consult with a qualified solicitor or lawyer before making legal decisions or signing contracts.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Upload;
